/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { Autobind } from "@Internal/Utils";
import { CustomError } from "@Internal/Models";

import { PriceVersion } from "App/Models";

import { ProcessException } from "./Base";

/* Read-side + actions for versioned price lists. The version records are returned by the backend as
   plain JSON (Status, RevisionPct, EffectiveDate, Amount); the cartouche only needs Status and the
   revision percentage, so they are consumed as-is without the annotated-JSON deserializer. */
export default class PriceVersions {
    private findService: Feathers.Service<any>;
    private publishService: Feathers.Service<any>;
    private reviseService: Feathers.Service<any>;

    constructor(
        findService: Feathers.Service<any>,
        publishService: Feathers.Service<any>,
        reviseService: Feathers.Service<any>
    ) {
        this.findService = findService;
        this.publishService = publishService;
        this.reviseService = reviseService;
    }

    @Autobind
    async FindByPrice(
        priceId: number
    ): Promise<[ PriceVersion[], CustomError | undefined ]> {
        try {
            const results = await this.findService.find( { query: { PriceId: priceId } } );

            /* The global serialization after-hook reshapes `{ Records, PagerSorter }` into
               `{ Items, Pagination }`, so read `Items` (keep `Records` as a defensive fallback). */
            const records = ( results !== undefined && results.Items !== undefined )
                ? results.Items
                : ( results !== undefined && results.Records !== undefined )
                    ? results.Records
                    : [];

            return [ records as PriceVersion[], undefined ];

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }
    }

    @Autobind
    async Publish(
        priceId: number,
        effectiveDate?: string
    ): Promise<CustomError | undefined> {
        try {
            const data: { EffectiveDate?: string } = {};

            if( effectiveDate !== undefined )
                data.EffectiveDate = effectiveDate;

            const results = await this.publishService.update( priceId, data );

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return;
    }

    @Autobind
    async Revise(
        priceId: number,
        revisionPct: number
    ): Promise<CustomError | undefined> {
        try {
            const results = await this.reviseService.update(
                priceId,
                { RevisionPct: revisionPct }
            );

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return;
    }
}
